import { useState, useRef, useCallback, type ChangeEvent } from "react";
import type { ScriptRunner } from "../engine/scriptRunner";
import TitleBlock from "./TitleBlock";
import DrawingToolbar from "./DrawingToolbar";
import AnnotationRenderer from "./AnnotationRenderer";
import { useAnnotationStore, type DrawingTool } from "./useAnnotationStore";
import { annotationId, type Vec2 } from "./annotationTypes";
import type { Annotation, LinearDimension, RadialDimension, DiameterDimension, CenterlineMark, LeaderNote, Balloon, TextNote } from "./annotationTypes";
import { exportDrawingPdf } from "./pdfExport";
import { downloadSvg } from "./svgExport";
import { downloadDxf } from "./dxfExport";
import { importDxf, type DxfImportResult } from "./dxfImport";
import { showToast } from "../ui/Toast";
import { useModal } from "../ui/Modal";
import {
  SHEET_W, SHEET_H, MARGIN, TITLE_BLOCK_H, TITLE_BLOCK_W,
  DRAWING_H, DRAWING_W,
} from "./constants";

interface DrawingCanvasProps {
  runner: ScriptRunner | null;
  docName: string;
  partNumber: string;
  revision: string;
}

interface ViewPaths {
  visiblePaths: string[];
  hiddenPaths: string[];
}

type ViewDirection = "front" | "top" | "right" | "iso";

const VIEW_DIRECTIONS: Record<ViewDirection, [number, number, number]> = {
  front: [0, 0, 1],
  top: [0, 1, 0],
  right: [1, 0, 0],
  iso: [1, 1, 1],
};

const VIEW_LABELS: Record<ViewDirection, string> = {
  front: "Front",
  top: "Top",
  right: "Right",
  iso: "Iso",
};

const QUADRANTS: Record<ViewDirection, { x: number; y: number; w: number; h: number }> = {
  front: { x: MARGIN, y: MARGIN, w: DRAWING_W / 2, h: DRAWING_H / 2 },
  right: { x: MARGIN + DRAWING_W / 2, y: MARGIN, w: DRAWING_W / 2, h: DRAWING_H / 2 },
  top: { x: MARGIN, y: MARGIN + DRAWING_H / 2, w: DRAWING_W / 2, h: DRAWING_H / 2 },
  iso: { x: MARGIN + DRAWING_W / 2, y: MARGIN + DRAWING_H / 2, w: DRAWING_W / 2, h: DRAWING_H / 2 },
};

/** Map a mouse event to SVG sheet coordinates */
function svgPoint(svg: SVGSVGElement, e: React.MouseEvent): Vec2 {
  const pt = svg.createSVGPoint();
  pt.x = e.clientX;
  pt.y = e.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return [0, 0];
  const transformed = pt.matrixTransform(ctm.inverse());
  return [transformed.x, transformed.y];
}

/** Determine which quadrant/view a point falls in */
function hitView(p: Vec2): ViewDirection | null {
  for (const [dir, q] of Object.entries(QUADRANTS) as [ViewDirection, typeof QUADRANTS["front"]][]) {
    if (p[0] >= q.x && p[0] <= q.x + q.w && p[1] >= q.y && p[1] <= q.y + q.h) {
      return dir;
    }
  }
  return null;
}

/** Split concatenated SVG path string from HLR into individual paths */
function splitPaths(raw: string | undefined): string[] {
  return raw ? raw.split(/(?=M )/) : [];
}

/** Build export filename from document metadata */
function exportFilename(partNumber: string, docName: string, revision: string, ext: string): string {
  return `${partNumber || docName || "drawing"}-${revision || "A"}.${ext}`;
}

const TOOL_CURSORS: Record<DrawingTool, string> = {
  select: "default",
  linear: "crosshair", radial: "crosshair", diameter: "crosshair",
  angular: "crosshair", ordinate: "crosshair",
  centerline: "crosshair",
  leader: "crosshair", balloon: "crosshair", text: "text",
  gdt: "crosshair", surfaceFinish: "crosshair",
};

export default function DrawingCanvas({ runner, docName, partNumber, revision }: DrawingCanvasProps) {
  const modal = useModal();
  const [views, setViews] = useState<Partial<Record<ViewDirection, ViewPaths>>>({});
  const [generating, setGenerating] = useState(false);
  const [importedPaths, setImportedPaths] = useState<string[]>([]);
  const [importedTexts, setImportedTexts] = useState<DxfImportResult["texts"]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const annStore = useAnnotationStore();

  // Click state for multi-click tools (e.g. linear = 2 clicks)
  const [clickState, setClickState] = useState<{ points: Vec2[]; viewId: string }>({ points: [], viewId: "" });

  const generateView = useCallback(async (dir: ViewDirection) => {
    if (!runner) return;
    setGenerating(true);
    try {
      const raw = await runner.generateDrawingView(VIEW_DIRECTIONS[dir]);
      setViews((prev) => ({
        ...prev,
        [dir]: { visiblePaths: splitPaths(raw.visiblePaths), hiddenPaths: splitPaths(raw.hiddenPaths) },
      }));
    } catch (err) {
      console.error(`HLR generation failed (${dir}):`, err);
      showToast(`Failed to generate ${VIEW_LABELS[dir]} view`, "error");
    } finally {
      setGenerating(false);
    }
  }, [runner]);

  const handleRegenerateAll = useCallback(async () => {
    if (!runner) return;
    setGenerating(true);
    try {
      const dirs: ViewDirection[] = ["front", "top", "right", "iso"];
      const results: Partial<Record<ViewDirection, ViewPaths>> = {};
      for (const dir of dirs) {
        const raw = await runner.generateDrawingView(VIEW_DIRECTIONS[dir]);
        results[dir] = { visiblePaths: splitPaths(raw.visiblePaths), hiddenPaths: splitPaths(raw.hiddenPaths) };
      }
      setViews(results);
    } catch (err) {
      console.error("HLR regeneration failed:", err);
      showToast("Failed to regenerate views", "error");
    } finally {
      setGenerating(false);
    }
  }, [runner]);

  const handleExportPdf = useCallback(async () => {
    if (!svgRef.current) return;
    try {
      await exportDrawingPdf(svgRef.current, exportFilename(partNumber, docName, revision, "pdf"));
      showToast("PDF exported", "success");
    } catch {
      showToast("PDF export failed", "error");
    }
  }, [partNumber, docName, revision]);

  const handleExportSvg = useCallback(() => {
    if (!svgRef.current) return;
    downloadSvg(svgRef.current, exportFilename(partNumber, docName, revision, "svg"));
    showToast("SVG exported", "success");
  }, [partNumber, docName, revision]);

  const handleExportDxf = useCallback(() => {
    const viewData = Object.entries(views).map(([dir, vp]) => {
      const q = QUADRANTS[dir as ViewDirection];
      return {
        visiblePaths: vp!.visiblePaths,
        hiddenPaths: vp!.hiddenPaths,
        offsetX: q.x + q.w / 2,
        offsetY: q.y + q.h / 2,
      };
    });
    downloadDxf(viewData, annStore.annotations, exportFilename(partNumber, docName, revision, "dxf"));
    showToast("DXF exported", "success");
  }, [views, annStore.annotations, partNumber, docName, revision]);

  const handleImportDxf = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = importDxf(reader.result as string);
        setImportedPaths(result.svgPaths);
        setImportedTexts(result.texts);
        showToast(`Imported ${result.entities.length} DXF entities`, "success");
      } catch (err) {
        showToast("DXF import failed", "error");
        console.error("DXF import error:", err);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }, []);

  // ── Click-to-place annotations ──────────────────────────

  const handleSvgClick = useCallback(async (e: React.MouseEvent<SVGSVGElement>) => {
    if (annStore.activeTool === "select") return;
    if (!svgRef.current) return;

    const p = svgPoint(svgRef.current, e);
    const viewDir = hitView(p);
    if (!viewDir) return;

    const q = QUADRANTS[viewDir];
    // Convert to view-local coordinates (relative to quadrant center)
    const localP: Vec2 = [p[0] - q.x - q.w / 2, p[1] - q.y - q.h / 2];
    const tool = annStore.activeTool;

    // Multi-click tools
    if (tool === "linear") {
      if (clickState.points.length === 0) {
        setClickState({ points: [localP], viewId: viewDir });
        return;
      }
      // Second click: create dimension
      const p1 = clickState.points[0];
      const ann: LinearDimension = {
        id: annotationId(), kind: "linear", viewId: viewDir,
        p1, p2: localP, offset: 8, orientation: "aligned",
      };
      annStore.addAnnotation(ann);
      setClickState({ points: [], viewId: "" });
      return;
    }

    if (tool === "angular") {
      const pts = clickState.points;
      if (pts.length < 2) {
        setClickState({ points: [...pts, localP], viewId: viewDir });
        return;
      }
      // Third click: vertex, p1, p2
      const ann: Annotation = {
        id: annotationId(), kind: "angular", viewId: viewDir,
        vertex: pts[0], p1: pts[1], p2: localP, arcRadius: 10,
      };
      annStore.addAnnotation(ann);
      setClickState({ points: [], viewId: "" });
      return;
    }

    if (tool === "leader") {
      const pts = clickState.points;
      if (pts.length < 1) {
        setClickState({ points: [localP], viewId: viewDir });
        return;
      }
      const text = (await modal.prompt("Note text:", "NOTE")) ?? "NOTE";
      const ann: LeaderNote = {
        id: annotationId(), kind: "leader", viewId: viewDir,
        points: [clickState.points[0], localP],
        text, fontSize: 3,
      };
      annStore.addAnnotation(ann);
      setClickState({ points: [], viewId: "" });
      return;
    }

    // Single-click tools
    if (tool === "radial") {
      const radius = parseFloat((await modal.prompt("Radius value:", "10")) ?? "10");
      const ann: RadialDimension = {
        id: annotationId(), kind: "radial", viewId: viewDir,
        center: localP, radius, leaderAngle: -45,
      };
      annStore.addAnnotation(ann);
    } else if (tool === "diameter") {
      const diameter = parseFloat((await modal.prompt("Diameter value:", "20")) ?? "20");
      const ann: DiameterDimension = {
        id: annotationId(), kind: "diameter", viewId: viewDir,
        center: localP, diameter, leaderAngle: 45,
      };
      annStore.addAnnotation(ann);
    } else if (tool === "ordinate") {
      const ann: Annotation = {
        id: annotationId(), kind: "ordinate", viewId: viewDir,
        origin: [0, 0], point: localP, axis: "x", jogOffset: 10,
      };
      annStore.addAnnotation(ann);
    } else if (tool === "centerline") {
      const ann: CenterlineMark = {
        id: annotationId(), kind: "centerline", viewId: viewDir,
        style: "cross", p1: localP, armLength: 5,
      };
      annStore.addAnnotation(ann);
    } else if (tool === "balloon") {
      const item = (await modal.prompt("Item number:", "1")) ?? "1";
      const ann: Balloon = {
        id: annotationId(), kind: "balloon", viewId: viewDir,
        center: [localP[0], localP[1] - 10], leaderTip: localP,
        itemNumber: item, radius: 4,
      };
      annStore.addAnnotation(ann);
    } else if (tool === "text") {
      const text = (await modal.prompt("Text:", "NOTE")) ?? "NOTE";
      const ann: TextNote = {
        id: annotationId(), kind: "text", viewId: viewDir,
        position: localP, text, fontSize: 3, fontWeight: "normal", rotation: 0,
      };
      annStore.addAnnotation(ann);
    } else if (tool === "gdt") {
      const tol = (await modal.prompt("Tolerance value:", "0.05")) ?? "0.05";
      const ann: Annotation = {
        id: annotationId(), kind: "gdt", viewId: viewDir,
        position: [localP[0], localP[1] - 10], leaderTip: localP,
        characteristic: "position", toleranceValue: tol, datumA: "A",
      };
      annStore.addAnnotation(ann);
    } else if (tool === "surfaceFinish") {
      const ra = (await modal.prompt("Roughness (Ra):", "1.6")) ?? "1.6";
      const ann: Annotation = {
        id: annotationId(), kind: "surfaceFinish", viewId: viewDir,
        position: [localP[0], localP[1] - 8], leaderTip: localP,
        roughness: ra,
      };
      annStore.addAnnotation(ann);
    }
  }, [annStore, clickState, modal]);

  // Delete selected annotation on Delete/Backspace
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === "Delete" || e.key === "Backspace") && annStore.selectedId) {
      annStore.deleteAnnotation(annStore.selectedId);
    }
    if (e.key === "Escape") {
      annStore.setActiveTool("select");
      setClickState({ points: [], viewId: "" });
    }
  }, [annStore]);

  const today = new Date().toISOString().slice(0, 10);
  const hasViews = Object.keys(views).length > 0;

  return (
    <div className="drawing-container" tabIndex={0} onKeyDown={handleKeyDown}>
      <DrawingToolbar
        activeTool={annStore.activeTool}
        onToolChange={(t) => { annStore.setActiveTool(t); setClickState({ points: [], viewId: "" }); }}
        generating={generating}
        hasRunner={!!runner}
        hasViews={hasViews}
        onGenerateView={generateView}
        onRegenerateAll={handleRegenerateAll}
        onExportPdf={handleExportPdf}
        onExportSvg={handleExportSvg}
        onExportDxf={handleExportDxf}
        onImportDxf={handleImportDxf}
      />

      {/* Hidden file input for DXF import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".dxf"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Tool hint bar */}
      {annStore.activeTool !== "select" && (
        <div className="dwg-hint-bar">
          {annStore.activeTool === "linear" && clickState.points.length === 0 && "Click first point of dimension"}
          {annStore.activeTool === "linear" && clickState.points.length === 1 && "Click second point of dimension"}
          {annStore.activeTool === "angular" && clickState.points.length === 0 && "Click vertex point"}
          {annStore.activeTool === "angular" && clickState.points.length === 1 && "Click first ray point"}
          {annStore.activeTool === "angular" && clickState.points.length === 2 && "Click second ray point"}
          {annStore.activeTool === "leader" && clickState.points.length === 0 && "Click text position"}
          {annStore.activeTool === "leader" && clickState.points.length === 1 && "Click arrow tip on geometry"}
          {["radial", "diameter", "ordinate", "centerline", "balloon", "text", "gdt", "surfaceFinish"].includes(annStore.activeTool) && "Click to place on drawing view"}
          <button className="dwg-hint-cancel" onClick={() => { annStore.setActiveTool("select"); setClickState({ points: [], viewId: "" }); }}>
            Esc to cancel
          </button>
        </div>
      )}

      {/* SVG Sheet */}
      <div className="drawing-sheet-wrap">
        <svg
          ref={svgRef}
          className="drawing-sheet"
          viewBox={`0 0 ${SHEET_W} ${SHEET_H}`}
          xmlns="http://www.w3.org/2000/svg"
          style={{ cursor: TOOL_CURSORS[annStore.activeTool] }}
          onClick={handleSvgClick}
        >
          {/* White background */}
          <rect x={0} y={0} width={SHEET_W} height={SHEET_H} fill="#fff" />

          {/* Border */}
          <rect x={MARGIN} y={MARGIN} width={SHEET_W - MARGIN * 2} height={SHEET_H - MARGIN * 2} fill="none" stroke="#000" strokeWidth={0.5} />

          {/* Quadrant dividers */}
          <line x1={MARGIN + DRAWING_W / 2} y1={MARGIN} x2={MARGIN + DRAWING_W / 2} y2={MARGIN + DRAWING_H} stroke="#ccc" strokeWidth={0.2} strokeDasharray="2 2" />
          <line x1={MARGIN} y1={MARGIN + DRAWING_H / 2} x2={MARGIN + DRAWING_W} y2={MARGIN + DRAWING_H / 2} stroke="#ccc" strokeWidth={0.2} strokeDasharray="2 2" />

          {/* Title block separator */}
          <line x1={MARGIN} y1={SHEET_H - MARGIN - TITLE_BLOCK_H} x2={SHEET_W - MARGIN} y2={SHEET_H - MARGIN - TITLE_BLOCK_H} stroke="#000" strokeWidth={0.3} />

          {/* Third-angle projection symbol */}
          <g transform={`translate(${MARGIN + 4}, ${SHEET_H - MARGIN - TITLE_BLOCK_H + 4})`}>
            <circle cx="0" cy="8" r="5" fill="none" stroke="#000" strokeWidth={0.3} />
            <line x1="-5" y1="8" x2="5" y2="8" stroke="#000" strokeWidth={0.3} />
            <line x1="0" y1="3" x2="0" y2="13" stroke="#000" strokeWidth={0.3} />
            <circle cx="14" cy="8" r="5" fill="none" stroke="#000" strokeWidth={0.3} />
            <circle cx="14" cy="8" r="2" fill="none" stroke="#000" strokeWidth={0.3} />
            <line x1="9" y1="8" x2="7" y2="8" stroke="#000" strokeWidth={0.3} />
            <text x="24" y="10" fontSize="2.5" fontFamily="monospace" fill="#666">3rd Angle</text>
          </g>

          {/* Render views */}
          {(Object.keys(QUADRANTS) as ViewDirection[]).map((dir) => {
            const vp = views[dir];
            const q = QUADRANTS[dir];
            if (!vp) {
              return (
                <text key={dir} x={q.x + q.w / 2} y={q.y + q.h / 2} textAnchor="middle" fontSize={4} fontFamily="monospace" fill="#ccc">
                  {VIEW_LABELS[dir]}
                </text>
              );
            }
            return (
              <g key={dir}>
                <text x={q.x + 2} y={q.y + 4} fontSize={3} fontFamily="monospace" fill="#999">
                  {VIEW_LABELS[dir]}
                </text>
                {vp.visiblePaths.map((d, i) => (
                  <path key={`v-${i}`} d={d} className="drawing-visible-edge" transform={`translate(${q.x + q.w / 2}, ${q.y + q.h / 2})`} />
                ))}
                {vp.hiddenPaths.map((d, i) => (
                  <path key={`h-${i}`} d={d} className="drawing-hidden-edge" transform={`translate(${q.x + q.w / 2}, ${q.y + q.h / 2})`} />
                ))}
              </g>
            );
          })}

          {/* Imported DXF geometry */}
          {importedPaths.map((d, i) => (
            <path key={`dxf-${i}`} d={d} className="drawing-visible-edge" />
          ))}
          {importedTexts.map((t, i) => (
            <text key={`dxft-${i}`} x={t.x} y={t.y} fontSize={t.height} fontFamily="monospace" fill="#000">{t.text}</text>
          ))}

          {/* Annotations */}
          {annStore.annotations.map((ann) => {
            const q = QUADRANTS[ann.viewId as ViewDirection];
            if (!q) return null;
            return (
              <AnnotationRenderer
                key={ann.id}
                annotation={ann}
                selected={ann.id === annStore.selectedId}
                offsetX={q.x + q.w / 2}
                offsetY={q.y + q.h / 2}
                onClick={() => annStore.setSelectedId(ann.id)}
              />
            );
          })}

          {/* Click preview dots for multi-click tools */}
          {clickState.points.map((pt, i) => {
            const q = QUADRANTS[clickState.viewId as ViewDirection];
            if (!q) return null;
            return (
              <circle
                key={`cp-${i}`}
                cx={q.x + q.w / 2 + pt[0]}
                cy={q.y + q.h / 2 + pt[1]}
                r={1}
                fill="var(--accent, #2196f3)"
                stroke="#fff"
                strokeWidth={0.3}
              />
            );
          })}

          {/* Title Block */}
          <TitleBlock
            partNumber={partNumber || "—"}
            revision={revision || "—"}
            name={docName || "—"}
            date={today}
            scale="1:1"
            x={SHEET_W - MARGIN - TITLE_BLOCK_W}
            y={SHEET_H - MARGIN - TITLE_BLOCK_H}
          />
        </svg>
      </div>
    </div>
  );
}
